// src/hooks/useWorkspacePresence.ts
// Workspace-level presence tracking using Supabase Realtime Presence
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase, isDemoMode } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { chatDebug } from '../utils/chatDebug';
import { markChannelJoined, markChannelLeft, incrementEvent } from '../utils/chatMetrics';
import { useIdleStatus } from './useIdleStatus';
import { getManualPresenceStatus, setManualPresenceStatus, type ManualPresenceStatus } from '../utils/presenceStatus';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { WorkspacePresenceEntry, PresenceStatus } from '../types/presence';

// Re-export for backwards compatibility
export type { PresenceStatus } from '../types/presence';

// Alias for backwards compatibility
export type PresenceUser = WorkspacePresenceEntry;

export interface UseWorkspacePresenceReturn {
  /** Array of user IDs currently online in the workspace */
  onlineUserIds: string[];
  /** Map of user_id to presence info */
  presenceByUserId: Record<string, WorkspacePresenceEntry>;
  /** Check if a specific user is online */
  isOnline: (userId: string) => boolean;
  /** Get last seen date for a user (null if never seen) */
  getLastSeen: (userId: string) => Date | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Current user's effective status */
  myStatus: PresenceStatus;
  /** Current manual override; null means automatic presence. */
  manualStatus: ManualPresenceStatus;
  /** Set manual status override (busy/dnd/null) */
  setManualStatus: (status: ManualPresenceStatus) => void;
}

// Heartbeat interval (30 seconds)
const HEARTBEAT_INTERVAL_MS = 30_000;
// Debounce for status changes (500ms)
const STATUS_UPDATE_DEBOUNCE_MS = 500;

interface ActivePresenceSubscription {
  channel: RealtimeChannel;
  channelName: string;
  heartbeatInterval: ReturnType<typeof setInterval> | null;
}

interface PendingPresenceCleanup {
  subscription: ActivePresenceSubscription;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Hook for tracking workspace-level presence using Supabase Realtime Presence.
 * 
 * Features:
 * - Joins a single presence channel per workspace (cheaper than per-room)
 * - Tracks online users with last_seen timestamps
 * - 30-second heartbeat to update last_seen_at
 * - Idle detection: 5 min no activity -> away
 * - Manual busy/DND modes (persisted in localStorage)
 * - Priority: manual override > idle > visibility > online
 * - Auto-cleanup on unmount/workspace change
 * - Demo mode: returns empty state, no channel subscription
 * 
 * @param workspaceId - The workspace ID to track presence for (null = no subscription)
 */
export function useWorkspacePresence(
  workspaceId: string | null
): UseWorkspacePresenceReturn {
  // Safe auth access — avoids crash during HMR when AuthContext reloads
  let user: { id: string; name?: string; avatar?: string; workspace_id?: string } | null = null;
  try {
    const auth = useAuth();
    user = auth.user;
  } catch {
    // AuthProvider not yet mounted (HMR race or bare mode)
  }
  const [presenceByUserId, setPresenceByUserId] = useState<Record<string, PresenceUser>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Idle detection
  const { isIdle } = useIdleStatus();

  // Manual status from localStorage
  const [manualStatus, setManualStatusState] = useState<ManualPresenceStatus>(() => getManualPresenceStatus(user?.id));
  const [isHidden, setIsHidden] = useState(() => document.hidden);
  useEffect(() => {
    const refresh = () => { setManualStatusState(getManualPresenceStatus(user?.id)); setIsHidden(document.hidden); };
    refresh();
    const timer = setInterval(refresh, 30_000);
    window.addEventListener('storage', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(timer); window.removeEventListener('storage', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [user?.id]);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const activeSubscriptionRef = useRef<ActivePresenceSubscription | null>(null);
  const pendingCleanupRef = useRef<PendingPresenceCleanup | null>(null);
  const visibilityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusUpdateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPublishedStatusRef = useRef<PresenceStatus>('online');

  // Calculate effective status based on priority: manual > idle > visibility > online
  const calculateEffectiveStatus = useCallback((): PresenceStatus => {
    // Manual Busy / Do not disturb has highest priority.
    if (manualStatus) {
      return manualStatus;
    }

    // Tab hidden -> away
    if (isHidden) {
      return 'away';
    }

    // Idle (5 min no activity) -> away
    if (isIdle) {
      return 'away';
    }

    // Default: online
    return 'online';
  }, [manualStatus, isIdle, isHidden]);

  // Current effective status
  const myStatus = useMemo(() => calculateEffectiveStatus(), [calculateEffectiveStatus]);

  // Set manual status (persists to localStorage)
  const setManualStatus = useCallback((status: ManualPresenceStatus) => {
    setManualPresenceStatus(status, user?.id);
    setManualStatusState(status);
  }, [user?.id]);

  // Build presence payload for current user
  const buildPresencePayload = useCallback((status: PresenceStatus = 'online'): PresenceUser => {
    return {
      user_id: user?.id || 'anonymous',
      display_name: user?.name || undefined,
      avatar_url: user?.avatar || undefined,
      status,
      last_seen_at: new Date().toISOString(),
    };
  }, [user?.id, user?.name, user?.avatar]);

  // Realtime channel setup is tied only to workspace/user identity. Status and
  // profile changes are published through refs so they do not rebuild a joined
  // channel whenever idle/manual presence changes.
  const effectiveStatusRef = useRef(myStatus);
  const presencePayloadBuilderRef = useRef(buildPresencePayload);
  effectiveStatusRef.current = myStatus;
  presencePayloadBuilderRef.current = buildPresencePayload;

  // Process presence state from Supabase into our format
  const processPresenceState = useCallback(
    (presenceState: Record<string, WorkspacePresenceEntry[]>) => {
      const newPresenceByUserId: Record<string, PresenceUser> = {};

      // Supabase presence state is keyed by presence_ref, each contains an array of payloads
      for (const presenceList of Object.values(presenceState)) {
        for (const presence of presenceList) {
          // Use most recent presence entry for each user
          const existing = newPresenceByUserId[presence.user_id];
          const presenceLastSeen = presence.last_seen_at ? new Date(presence.last_seen_at) : new Date(0);
          const existingLastSeen = existing?.last_seen_at ? new Date(existing.last_seen_at) : new Date(0);
          if (!existing || presenceLastSeen > existingLastSeen) {
            newPresenceByUserId[presence.user_id] = {
              user_id: presence.user_id,
              display_name: presence.display_name,
              avatar_url: presence.avatar_url,
              status: presence.status,
              last_seen_at: presence.last_seen_at,
            };
          }
        }
      }

      setPresenceByUserId(newPresenceByUserId);
    },
    []
  );

  // Subscribe to presence channel
  useEffect(() => {
    // Demo mode or no supabase: skip realtime, return empty state
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    // No workspace or user: skip
    if (!workspaceId || !user?.id) {
      setLoading(false);
      return;
    }

    const channelName = `presence:workspace:${workspaceId}`;

    const scheduleCleanup = (subscription: ActivePresenceSubscription) => {
      if (pendingCleanupRef.current?.subscription === subscription) return;

      const timer = setTimeout(() => {
        if (subscription.heartbeatInterval) {
          clearInterval(subscription.heartbeatInterval);
          subscription.heartbeatInterval = null;
        }
        chatDebug.channel(`Presence channel LEAVING`, { workspaceId });
        markChannelLeft(subscription.channelName);
        void supabase.removeChannel(subscription.channel);

        if (activeSubscriptionRef.current === subscription) {
          activeSubscriptionRef.current = null;
        }
        if (channelRef.current === subscription.channel) {
          channelRef.current = null;
        }
        if (pendingCleanupRef.current?.subscription === subscription) {
          pendingCleanupRef.current = null;
        }
      }, 0);

      pendingCleanupRef.current = { subscription, timer };
    };

    const existingSubscription = activeSubscriptionRef.current;
    const pendingCleanup = pendingCleanupRef.current;

    // React Strict Mode replays effects in development. Keep the already joined
    // channel alive across that immediate replay instead of registering a second
    // set of callbacks on the same Supabase topic.
    if (existingSubscription?.channelName === channelName) {
      if (pendingCleanup?.subscription === existingSubscription) {
        clearTimeout(pendingCleanup.timer);
        pendingCleanupRef.current = null;
      }
      channelRef.current = existingSubscription.channel;
      return () => scheduleCleanup(existingSubscription);
    }

    // A genuine workspace/user change lets the prior scheduled cleanup finish
    // while the new topic gets its own channel.
    if (existingSubscription) {
      if (!pendingCleanup || pendingCleanup.subscription !== existingSubscription) {
        scheduleCleanup(existingSubscription);
      }
      activeSubscriptionRef.current = null;
      if (channelRef.current === existingSubscription.channel) {
        channelRef.current = null;
      }
    }

    setLoading(true);
    setError(null);

    const channel = supabase.channel(channelName);
    const subscription: ActivePresenceSubscription = {
      channel,
      channelName,
      heartbeatInterval: null,
    };

    activeSubscriptionRef.current = subscription;
    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        processPresenceState(state as unknown as Record<string, WorkspacePresenceEntry[]>);
        const onlineCount = Object.keys(state).length;
        chatDebug.presence(`Sync`, { onlineCount });
        incrementEvent('presence', 'SYNC');
        setLoading(false);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        chatDebug.presence(`User joined`, { count: (newPresences as unknown[]).length });
        // Update with new presence entries
        setPresenceByUserId((prev) => {
          const updated = { ...prev };
          for (const presence of newPresences as unknown as PresenceUser[]) {
            updated[presence.user_id] = presence;
          }
          return updated;
        });
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        chatDebug.presence(`User left`, { count: (leftPresences as unknown[]).length });
        // Remove left users (but keep last_seen for getLastSeen)
        setPresenceByUserId((prev) => {
          const updated = { ...prev };
          for (const presence of leftPresences as unknown as PresenceUser[]) {
            // Mark as offline but keep last_seen
            if (updated[presence.user_id]) {
              updated[presence.user_id] = {
                ...updated[presence.user_id],
                status: 'away',
              };
            }
          }
          return updated;
        });
      })
      .subscribe(async (status) => {
        if (activeSubscriptionRef.current !== subscription) return;

        if (status === 'SUBSCRIBED') {
          chatDebug.channel(`Presence channel JOINED`, { workspaceId });
          markChannelJoined(`presence:workspace:${workspaceId}`);
          // Track our presence with current effective status
          const initialStatus = effectiveStatusRef.current;
          lastPublishedStatusRef.current = initialStatus;
          const payload = presencePayloadBuilderRef.current(initialStatus);
          await channel.track(payload);

          if (activeSubscriptionRef.current !== subscription) return;

          // Start heartbeat to update last_seen_at every 30s
          if (subscription.heartbeatInterval) {
            clearInterval(subscription.heartbeatInterval);
          }
          subscription.heartbeatInterval = setInterval(async () => {
            const currentStatus = effectiveStatusRef.current;
            lastPublishedStatusRef.current = currentStatus;
            const updatedPayload = presencePayloadBuilderRef.current(currentStatus);
            await channel.track(updatedPayload);
          }, HEARTBEAT_INTERVAL_MS);
        } else if (status === 'CHANNEL_ERROR') {
          chatDebug.channel(`Presence channel ERROR`, { workspaceId });
          setError('Failed to connect to presence channel');
          setLoading(false);
        }
      });

    // Cleanup on unmount or workspace change
    return () => scheduleCleanup(subscription);
  }, [workspaceId, user?.id, processPresenceState]);

  // ─── Visibility-based status (online/away) ─────────────────────────────────
  useEffect(() => {
    // Skip in demo mode or if no channel
    if (isDemoMode || !channelRef.current) {
      return;
    }

    const handleVisibilityChange = () => {
      // Debounce to avoid spamming on rapid tab switches
      if (visibilityDebounceRef.current) {
        clearTimeout(visibilityDebounceRef.current);
      }

      visibilityDebounceRef.current = setTimeout(async () => {
        const channel = channelRef.current;
        if (!channel) return;

        // Use calculated effective status instead of just visibility
        const newStatus = calculateEffectiveStatus();

        // Only publish if status actually changed
        if (newStatus !== lastPublishedStatusRef.current) {
          chatDebug.presence(`Status changed`, { newStatus, previous: lastPublishedStatusRef.current });
          lastPublishedStatusRef.current = newStatus;
          const payload = buildPresencePayload(newStatus);
          await channel.track(payload);
        }
      }, STATUS_UPDATE_DEBOUNCE_MS);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityDebounceRef.current) {
        clearTimeout(visibilityDebounceRef.current);
        visibilityDebounceRef.current = null;
      }
    };
  }, [buildPresencePayload, calculateEffectiveStatus]);

  // ─── Idle/Manual status change handler ─────────────────────────────────────
  useEffect(() => {
    // Skip in demo mode or if no channel
    if (isDemoMode || !channelRef.current) {
      return;
    }

    // Debounce status updates to avoid spamming
    if (statusUpdateDebounceRef.current) {
      clearTimeout(statusUpdateDebounceRef.current);
    }

    statusUpdateDebounceRef.current = setTimeout(async () => {
      const channel = channelRef.current;
      if (!channel) return;

      const newStatus = calculateEffectiveStatus();

      // Only publish if status actually changed
      if (newStatus !== lastPublishedStatusRef.current) {
        chatDebug.presence(`Status changed (idle/manual)`, { newStatus, previous: lastPublishedStatusRef.current });
        lastPublishedStatusRef.current = newStatus;
        const payload = buildPresencePayload(newStatus);
        await channel.track(payload);
      }
    }, STATUS_UPDATE_DEBOUNCE_MS);

    return () => {
      if (statusUpdateDebounceRef.current) {
        clearTimeout(statusUpdateDebounceRef.current);
        statusUpdateDebounceRef.current = null;
      }
    };
  }, [isIdle, manualStatus, buildPresencePayload, calculateEffectiveStatus]);

  // Derived: list of online user IDs
  const onlineUserIds = useMemo(() => {
    return Object.values(presenceByUserId)
      .filter((p) => p.status === 'online' || p.status === 'busy' || p.status === 'dnd')
      .map((p) => p.user_id);
  }, [presenceByUserId]);

  // Check if a specific user is connected (busy and DND still count as online)
  const isOnline = useCallback(
    (userId: string): boolean => {
      const status = presenceByUserId[userId]?.status;
      return status === 'online' || status === 'busy' || status === 'dnd';
    },
    [presenceByUserId]
  );

  // Get last seen date for a user
  const getLastSeen = useCallback(
    (userId: string): Date | null => {
      const presence = presenceByUserId[userId];
      if (!presence?.last_seen_at) return null;
      return new Date(presence.last_seen_at);
    },
    [presenceByUserId]
  );

  return {
    onlineUserIds,
    presenceByUserId,
    isOnline,
    getLastSeen,
    loading,
    error,
    myStatus,
    manualStatus,
    setManualStatus,
  };
}

export default useWorkspacePresence;
